import i18next from 'i18next';
import enTranslations from '../translations/en-GB.json';

const generateTranslationImport = (lng) => {
  const LOCALES = {
    'ar-SA': 'ar-SA',
    'bg-BG': 'bg-BG',
    'bs-BA': 'bs-BA',
    'cs-CZ': 'cs-CZ',
    'da-DK': 'da-DK',
    'de-DE': 'de-DE',
    'el-GR': 'el-GR',
    'en-GB': 'en-GB',
    'en-US': 'en-US',
    'es-ES': 'es-ES',
    'es-MX': 'es-MX',
    'et-EE': 'et-EE',
    'fi-FI': 'fi-FI',
    'fp-PH': 'fp-PH',
    'fr-CA': 'fr-CA',
    'fr-FR': 'fr-FR',
    'gu-IN': 'gu-IN',
    'he-IL': 'he-IL',
    'hr-HR': 'hr-HR',
    'hu-HU': 'hu-HU',
    'id-ID': 'id-ID',
    'it-IT': 'it-IT',
    'ja-JP': 'ja-JP',
    'ka-GE': 'ka-GE',
    'kk-KZ': 'kk-KZ',
    'ko-KR': 'ko-KR',
    'lt-LT': 'lt-LT',
    'lv-LV': 'lv-LV',
    'me-ME': 'me-ME',
    'mk-MK': 'mk-MK',
    'ms-MY': 'ms-MY',
    'nl-NL': 'nl-NL',
    'nb-NO': 'nn-NO',
    'no-NO': 'nn-NO',
    'nn-NO': 'nn-NO',
    'pl-PL': 'pl-PL',
    'pt-BR': 'pt-BR',
    'pt-PT': 'pt-PT',
    'ro-RO': 'ro-RO',
    'ru-RU': 'ru-RU',
    'sk-SK': 'sk-SK',
    'sl-SI': 'sl-SI',
    'sq-AL': 'sq-AL',
    'sr-RS': 'sr-SP',
    'sr-SP': 'sr-SP',
    'sr-YR': 'sr-YR',
    'sv-SE': 'sv-SE',
    'th-TH': 'th-TH',
    'tr-TR': 'tr-TR',
    'uk-UA': 'uk-UA',
    'vi-VN': 'vi-VN',
    'zh-CN': 'zh-CN',
    'zh-TW': 'zh-TW'
  };
  const LANGUAGES = {
    ar: LOCALES['ar-SA'],
    bg: LOCALES['bg-BG'],
    bs: LOCALES['bs-BA'],
    cs: LOCALES['cs-CZ'],
    da: LOCALES['da-DK'],
    de: LOCALES['de-DE'],
    el: LOCALES['el-GR'],
    en: LOCALES['en-GB'],
    es: LOCALES['es-ES'],
    et: LOCALES['et-EE'],
    fi: LOCALES['fi-FI'],
    fp: LOCALES['fp-PH'],
    fr: LOCALES['fr-FR'],
    gu: LOCALES['gu-IN'],
    he: LOCALES['he-IL'],
    hr: LOCALES['hr-HR'],
    hu: LOCALES['hu-HU'],
    id: LOCALES['id-ID'],
    it: LOCALES['it-IT'],
    ja: LOCALES['ja-JP'],
    ka: LOCALES['ka-GE'],
    kk: LOCALES['kk-KZ'],
    ko: LOCALES['ko-KR'],
    lt: LOCALES['lt-LT'],
    lv: LOCALES['lv-LV'],
    me: LOCALES['me-ME'],
    mk: LOCALES['mk-MK'],
    ms: LOCALES['ms-MY'],
    nl: LOCALES['nl-NL'],
    nn: LOCALES['nn-NO'],
    pl: LOCALES['pl-PL'],
    pt: LOCALES['pt-PT'],
    ro: LOCALES['ro-RO'],
    ru: LOCALES['ru-RU'],
    sk: LOCALES['sk-SK'],
    sl: LOCALES['sl-SI'],
    sq: LOCALES['sq-AL'],
    sr: LOCALES['sr-SP'],
    sv: LOCALES['sv-SE'],
    th: LOCALES['th-TH'],
    tr: LOCALES['tr-TR'],
    uk: LOCALES['uk-UA'],
    vi: LOCALES['vi-VN'],
    zh: LOCALES['zh-CN']
  };
  const defaultLocale = LOCALES['en-GB'];
  if (!lng || typeof lng !== 'string') return defaultLocale;
  const returnValue = LOCALES[lng] || LANGUAGES[lng] || defaultLocale;
  return returnValue;
};

const generateTranslation = (lng, { default: translation }) => {
  return {
    [lng]: {
      translation
    }
  };
};

const config = (lng) =>
  new Promise((resolve) => {
    return import(`../translations/${generateTranslationImport(lng)}.json`)
      .then((module) => {
        const translation = generateTranslation(lng, module);
        return resolve(translation);
      })
      .catch((module) => {
        const translation = generateTranslation(lng, module);
        return resolve(translation);
      });
  });

const createFallbackLanguage = async (lng) => {
  let fallbackLanguage = {};
  switch (lng) {
    case 'fr-CA':
      // eslint-disable-next-line no-case-declarations
      const frFr = await config('fr-FR');
      fallbackLanguage = {
        ...frFr,
        ...fallbackLanguage
      };
      break;
    default:
      // Do nothing
      break;
  }
  return fallbackLanguage;
};

const setupTranslations = async (lng = 'en-GB') => {
  let fallbackToEn = await createFallbackLanguage(lng);
  const translation = await config(lng);
  const defaultLang =
    lng === 'en' || lng === 'en-GB' ? {} : await config('en-GB');

  fallbackToEn = {
    ...fallbackToEn,
    ...defaultLang
  };

  return i18next.init({
    fallbackLng: {
      'fr-CA': ['fr-FR', 'en-GB'],
      default: ['en-GB']
    },
    lng,
    debug: false,
    returnEmptyString: false,
    returnNull: false,
    resources: {
      ...translation,
      ...fallbackToEn
    }
  });
};

// Required for testing environments (e.g. integration tests)
// as these don't support conditional imports
export const setupTranslationsForTesting = () => {
  return i18next.init({
    fallbackLng: 'en-GB',
    lng: 'en-GB',
    resources: {
      en: {
        translation: {
          ...enTranslations
        }
      }
    }
  });
};

export default setupTranslations;
